import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ClickAwayListener } from 'components/authentication/ClickAwayListener';
import { Manager, Reference, Popper } from 'react-popper';
import { Modifiers } from 'popper.js';
import { createPanResponder } from 'helpers/pan-responder';
import { ShowAt } from 'consts/breakpoints';
import CloseIcon from 'assets/svg/interface-16-close-1-5.svg';
import { noop } from 'lodash';
import { TooltipWrapper, TooltipArrow } from '../tooltip-container/styled';
import { TooltipProps } from '../types';


const LONG_TAP_THRESHOLD = 150;
const PERSISTENT_DELAY = 100;


const Tooltip: React.FC<TooltipProps> = ({
  children,
  tooltip,
  placement,
  size,
  hideArrow,
  raw,
  wrapperStyle,
  tapThreshold = LONG_TAP_THRESHOLD,
  clickable,
  disableClickAway,
  showOnLoad = false,
  persistent = false,
}) => {
  const [ tooltipVisible, setTooltipVisible ] = useState(showOnLoad);
  const [ hasBeenClicked, setHasBeenClicked ] = useState(false);

  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
    }
    if (!tooltipVisible) setTooltipVisible(true);
  }, [ tooltipVisible ]);

  const handleMouseLeave = useCallback(() => {
    if (persistent) {
      leaveTimeoutRef.current = setTimeout(() => {
        setTooltipVisible(false);
      }, PERSISTENT_DELAY);
    }
    else if (hasBeenClicked) {
      return;
    }
    else {
      setTooltipVisible(false);
    }
  }, [ hasBeenClicked, persistent ]);

  const handleAwayClick = useCallback(() => {
    setTooltipVisible(false);
    setHasBeenClicked(false);
  }, []);

  useEffect(() => {
    return () => {
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (clickable) {
      setTooltipVisible(true);
    }
  }, [ clickable ]);

  const touchStartTime = useRef(null);
  const touchHandlers = clickable ? null : useRef(createPanResponder({
    onStart: () => {
      touchStartTime.current = +Date.now();
      setTooltipVisible(false);
      setHasBeenClicked(false);
    },
    onMove: (state, e) => {
      if (!state.down) {
        const isLongTap = (+Date.now() - touchStartTime.current) > tapThreshold;
        if (isLongTap) {
          setTooltipVisible(true);
          e.preventDefault();
        }
      }
    },
    onEnd: () => {
      touchStartTime.current = null;
    },
  }));

  const mouseHandlers = clickable ? null : {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  };

  const modifiers: Modifiers = {
    preventOverflow: {
      boundariesElement: 'viewport',
    },
  };

  return (
    <Manager>
      <ShowAt at={1}>
        {isMobile => (
          <Reference>
            {({ ref }) => (
              <div
                {...(isMobile ? touchHandlers.current : mouseHandlers)}
                className="tooltip-wrapper"
                ref={ref}
              >
                {children}
              </div>
            )}
          </Reference>
        )}
      </ShowAt>
      {tooltipVisible ? (
        <ClickAwayListener onClickAway={disableClickAway ? noop : handleAwayClick}>
          <Popper
            placement={placement || 'top'}
            positionFixed
            modifiers={modifiers}
          >
            {({ ref, style, placement: tooltipPlacement, arrowProps }) => (
              raw ? (
                ReactDOM.createPortal(
                  <div
                    ref={ref}
                    style={{ ...style, zIndex: 102 }}
                    data-placement={placement}
                    onMouseEnter={persistent ? handleMouseEnter : undefined}
                    onMouseLeave={persistent ? handleMouseLeave : undefined}
                  >
                    {tooltip}
                  </div>, document.getElementById('sticky-root'))
              ) : (
                ReactDOM.createPortal(
                  <TooltipWrapper
                    ref={ref}
                    style={{ ...style, ...wrapperStyle }}
                    data-placement={tooltipPlacement}
                    onClick={clickable ? undefined : handleAwayClick}
                    size={size}
                    hideArrow={hideArrow}
                    clickable={clickable}
                    onMouseEnter={persistent ? handleMouseEnter : undefined}
                    onMouseLeave={persistent ? handleMouseLeave : undefined}
                  >
                    {tooltip}
                    {!hideArrow && (
                      <>
                        <TooltipArrow
                          placement={tooltipPlacement}
                          ref={arrowProps.ref}
                          style={arrowProps.style}
                        />
                        {clickable ? (
                          <div style={{ cursor: 'pointer' }}>
                            <CloseIcon data-auto="tooltip-close-button" onClick={handleAwayClick} />
                          </div>
                        ) : null}
                      </>
                    )}
                  </TooltipWrapper>, document.getElementById('sticky-root'))
              ))}
          </Popper>
        </ClickAwayListener>
      ) : null}
    </Manager>
  );
};

export default Tooltip;
